import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
} from 'date-fns';
import { clampDateRange, emptyDateRange, type DateRangeValue } from '@/shared/utils/dateRange';

export type DateRangePresetId =
  | 'today'
  | 'yesterday'
  | 'last7Days'
  | 'lastWeek'
  | 'last30Days'
  | 'lastMonth'
  | 'thisMonth'
  /** Full calendar month (1st through last day), not month-to-date. */
  | 'calendarMonth'
  | 'lastQuarter'
  | 'all';

export const DATE_RANGE_PRESET_LABELS: Record<DateRangePresetId, string> = {
  today: 'Hôm nay',
  yesterday: 'Hôm qua',
  last7Days: '7 ngày qua',
  lastWeek: 'Tuần trước',
  last30Days: '30 ngày qua',
  lastMonth: 'Tháng trước',
  thisMonth: 'Tháng này',
  calendarMonth: 'Tháng này',
  lastQuarter: 'Quý trước',
  all: 'Tất cả',
};

/** Menu `DateRangeField` shows unless a screen passes a shorter `presets` list. */
export const DEFAULT_DATE_RANGE_PRESETS: DateRangePresetId[] = [
  'today',
  'yesterday',
  'lastWeek',
  'lastMonth',
  'lastQuarter',
  'calendarMonth',
  'all',
];

export function getDateRangePreset(id: DateRangePresetId, today = new Date()): DateRangeValue {
  const day = startOfDay(today);

  switch (id) {
    case 'today':
      return clampDateRange({ from: day, to: day });
    case 'yesterday': {
      const yesterday = subDays(day, 1);
      return clampDateRange({ from: yesterday, to: yesterday });
    }
    case 'last7Days':
      return clampDateRange({ from: subDays(day, 7), to: day });
    case 'lastWeek': {
      const previousWeek = subWeeks(day, 1);
      return clampDateRange({
        from: startOfWeek(previousWeek, { weekStartsOn: 1 }),
        to: endOfWeek(previousWeek, { weekStartsOn: 1 }),
      });
    }
    case 'last30Days':
      return clampDateRange({ from: subDays(day, 30), to: day });
    case 'lastMonth': {
      const previousMonth = subMonths(day, 1);
      return clampDateRange({
        from: startOfMonth(previousMonth),
        to: endOfMonth(previousMonth),
      });
    }
    case 'thisMonth':
      return clampDateRange({ from: startOfMonth(day), to: day });
    case 'calendarMonth':
      return clampDateRange({ from: startOfMonth(day), to: endOfMonth(day) });
    case 'lastQuarter': {
      const previousQuarter = subQuarters(day, 1);
      return clampDateRange({
        from: startOfQuarter(previousQuarter),
        to: endOfQuarter(previousQuarter),
      });
    }
    case 'all':
      return emptyDateRange();
    default:
      return clampDateRange({ from: day, to: day });
  }
}

export function isSameDateRangeValue(a: DateRangeValue, b: DateRangeValue): boolean {
  const fromA = a.from?.getTime() ?? null;
  const fromB = b.from?.getTime() ?? null;
  const toA = a.to?.getTime() ?? null;
  const toB = b.to?.getTime() ?? null;
  return fromA === fromB && toA === toB;
}
